import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { BaseInput, BaseSelect, BaseButton, BaseTextarea } from "../../components/ui";
import "../../assets/styles/inmueble-form.css";
import inmuebleService from "../../services/inmuebleService";
import personaService from "../../services/personaService";
import tipoInmuebleService from "../../services/tipoInmuebleService";
import LocationMapPicker from "../../components/maps/LocationMapPicker";
import geocodingService from "../../services/geocodingService";
import { hasValidCoordinates } from "../../components/maps/openStreetMapUtils";

const FormSection = ({ title, description, children }) => (
  <section className="inmueble-form__section">
    <div className="inmueble-form__section-head">
      <h5>{title}</h5>
      {description && <p>{description}</p>}
    </div>
    <div className="row g-3">{children}</div>
  </section>
);

const EditarInmueble = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    propietario_id: '',
    tipoInmueble_id: '',
    nombre: '',
    descripcion: '',
    direccion: '',
    ubigeo: '',
    city: '',
    province: '',
    country: 'Perú',
    latitude: '',
    longitude: '',
    show_on_map: true,
  });
  const [propietarios, setPropietarios] = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const inmueble = await inmuebleService.obtenerInmueblePorId(id);
        const personas = await personaService.obtenerPersonas();
        const tiposInmuebleData = await tipoInmuebleService.obtenertipoInmueble();

        const propietariosFiltrados = personas.filter(p => p.rol === "propietario");

        setFormData({
          propietario_id: inmueble.propietario_id,
          tipoInmueble_id: inmueble.tipoInmueble_id,
          nombre: inmueble.nombre || '',
          descripcion: inmueble.descripcion || '',
          direccion: inmueble.direccion || '',
          ubigeo: inmueble.ubigeo || '',
          city: inmueble.city || '',
          province: inmueble.province || '',
          country: inmueble.country || 'Perú',
          latitude: inmueble.latitude ?? '',
          longitude: inmueble.longitude ?? '',
          show_on_map: inmueble.show_on_map ?? true,
        });
        setPropietarios(propietariosFiltrados);
        setTiposInmueble(tiposInmuebleData);
        setLoading(false);

      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleMapLocationChange = (location) => {
    setFormData((prev) => ({
      ...prev,
      latitude: location.latitude ?? prev.latitude,
      longitude: location.longitude ?? prev.longitude,
      city: location.city ?? prev.city,
      province: location.province ?? prev.province,
      country: location.country ?? prev.country,
    }));
  };

  const handleGeocodeAddress = async () => {
    const query = [formData.direccion, formData.city, formData.country].filter(Boolean).join(', ');
    if (!query.trim()) return;

    try {
      const location = await geocodingService.search(query);
      handleMapLocationChange(location);
    } catch {
      alert('No se encontró la dirección en el mapa.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const camposValidos = [
      'propietario_id', 'tipoInmueble_id', 'nombre', 'descripcion', 'direccion', 'ubigeo',
      'city', 'province', 'country', 'latitude', 'longitude', 'show_on_map',
    ];
  
    const datosFiltrados = Object.fromEntries(
      Object.entries(formData).filter(([key]) => camposValidos.includes(key))
    );

    if (datosFiltrados.show_on_map && !hasValidCoordinates(datosFiltrados.latitude, datosFiltrados.longitude)) {
      alert('Marque la ubicación del edificio en el mapa para mostrarlo en el landing.');
      return;
    }

    if (datosFiltrados.latitude !== '' && datosFiltrados.latitude != null) {
      datosFiltrados.latitude = Number(datosFiltrados.latitude);
    }
    if (datosFiltrados.longitude !== '' && datosFiltrados.longitude != null) {
      datosFiltrados.longitude = Number(datosFiltrados.longitude);
    }
    datosFiltrados.show_on_map = Boolean(datosFiltrados.show_on_map);
  
    try {
      setSaving(true);
      await inmuebleService.actualizarInmueble(id, datosFiltrados);
      alert('Inmueble actualizado correctamente');
      navigate("/inmueble-registros");
    } catch (error) {
      console.error("Error updating inmueble:", error);
      alert('Error al actualizar el inmueble');
       // Verificar si hay una respuesta del servidor
          if (error.response) {
            console.error('Código de estado:', error.response.status);
            console.error('Mensaje de error:', error.response.data);
            alert(`Error ${error.response.status}: ${JSON.stringify(error.response.data)}`);
          } else if (error.request) {
            console.error('No hubo respuesta del servidor:', error.request);
            alert('Error: No hubo respuesta del servidor.');
          } else {
            console.error('Error en la configuración de la solicitud:', error.message);
            alert(`Error: ${error.message}`);
          }
    } finally {
      setSaving(false);
    }
  };

  const propietarioOptions = propietarios.map((p) => ({
    value: p.id,
    label: `${p.nombre} ${p.apellido}`,
  }));

  const tipoOptions = tiposInmueble.map((tipo) => ({
    value: tipo.id,
    label: tipo.nombre,
  }));

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="inmueble-form-page">
      <Header />
      <Sidebar id="menu-item1" id1="menu-items1" activeClassName="inmueble-editar" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item active">Editar inmueble</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="card inmueble-form-card">
                <form onSubmit={handleSubmit} noValidate>
                  <FormSection
                    title="Datos generales"
                    description="Información básica del edificio y su clasificación."
                  >
                    <div className="col-12 col-lg-8">
                      <BaseInput
                        label="Nombre del inmueble"
                        name="nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <BaseSelect
                        label="Tipo"
                        name="tipoInmueble_id"
                        required
                        isSearchable
                        options={tipoOptions}
                        value={formData.tipoInmueble_id}
                        onChange={handleChange}
                        placeholder="Seleccionar"
                      />
                    </div>

                    <div className="col-12 col-lg-6">
                      <BaseSelect
                        label="Propietario"
                        name="propietario_id"
                        required
                        isSearchable
                        options={propietarioOptions}
                        value={formData.propietario_id}
                        onChange={handleChange}
                        placeholder="Seleccionar propietario"
                      />
                    </div>

                    <div className="col-12">
                      <BaseTextarea
                        label="Descripción"
                        name="descripcion"
                        required
                        rows={3}
                        value={formData.descripcion}
                        onChange={handleChange}
                      />
                    </div>
                  </FormSection>

                  <FormSection
                    title="Dirección"
                    description="Ubicación administrativa del inmueble."
                  >
                    <div className="col-12 col-lg-8">
                      <BaseInput
                        label="Dirección"
                        name="direccion"
                        required
                        value={formData.direccion}
                        onChange={handleChange}
                        groupEnd={
                          <BaseButton type="button" variant="outline" onClick={handleGeocodeAddress}>
                            Buscar en mapa
                          </BaseButton>
                        }
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <BaseInput
                        label="Ubigeo"
                        name="ubigeo"
                        required
                        restrict="number"
                        maxLength={6}
                        value={formData.ubigeo}
                        onChange={handleChange}
                      />
                    </div>
                  </FormSection>

                  <FormSection
                    title="Ubicación en mapa"
                    description="Marca el punto exacto del edificio. Se usará en el mapa público de RentAHAB."
                  >
                    <div className="col-12">
                      <div className="inmueble-form__location-grid">
                        <div className="inmueble-form__map-panel">
                          <BaseInput
                            label="Ciudad"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                          />
                          <BaseInput
                            label="Provincia / Departamento"
                            name="province"
                            value={formData.province}
                            onChange={handleChange}
                          />
                          <BaseInput
                            label="País"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="inmueble-form__map-canvas">
                          <LocationMapPicker
                            latitude={formData.latitude}
                            longitude={formData.longitude}
                            onChange={handleMapLocationChange}
                            height={320}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="inmueble-form__checkbox">
                        <input
                          type="checkbox"
                          name="show_on_map"
                          checked={Boolean(formData.show_on_map)}
                          onChange={handleChange}
                        />
                        Mostrar en mapa público de RentAHAB
                      </label>
                    </div>
                  </FormSection>

                  <div className="inmueble-form__actions">
                    <BaseButton
                      type="button"
                      variant="secondary"
                      onClick={() => navigate("/inmueble-registros")}
                    >
                      Cancelar
                    </BaseButton>
                    <BaseButton type="submit" variant="primary" loading={saving} disabled={saving}>
                      Guardar
                    </BaseButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarInmueble;
